import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import './ImportEventPage.css';

const ImportEventPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Form state
  const [eventLink, setEventLink] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventSeries, setEventSeries] = useState('');
  const [edition, setEdition] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [eventImageUrl, setEventImageUrl] = useState('');
  const [selectedOrganizers, setSelectedOrganizers] = useState([]);
  const [organizerInput, setOrganizerInput] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [ticketLink, setTicketLink] = useState('');
  const [peopleResponse, setPeopleResponse] = useState('');

  // Currency options
  const currencyOptions = [
    { code: 'EUR', symbol: '€', label: 'EUR (€)' },
    { code: 'USD', symbol: '$', label: 'USD ($)' },
    { code: 'GBP', symbol: '£', label: 'GBP (£)' },
    { code: 'CHF', symbol: 'CHF', label: 'CHF' },
    { code: 'PLN', symbol: 'zł', label: 'PLN (zł)' },
    { code: 'CZK', symbol: 'Kč', label: 'CZK (Kč)' },
    { code: 'SEK', symbol: 'kr', label: 'SEK (kr)' },
    { code: 'NOK', symbol: 'kr', label: 'NOK (kr)' },
    { code: 'DKK', symbol: 'kr', label: 'DKK (kr)' },
    { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
    { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
    { code: 'BRL', symbol: 'R$', label: 'BRL (R$)' },
    { code: 'MXN', symbol: 'MX$', label: 'MXN (MX$)' },
  ];

  // Tags state
  const [selectedDanceStyles, setSelectedDanceStyles] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [danceStyleInput, setDanceStyleInput] = useState('');
  const [artistInput, setArtistInput] = useState('');

  // Autocomplete suggestions state
  const [danceStyleSuggestions, setDanceStyleSuggestions] = useState([]);
  const [artistSuggestions, setArtistSuggestions] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [organizerSuggestions, setOrganizerSuggestions] = useState([]);
  const [eventSeriesSuggestions, setEventSeriesSuggestions] = useState([]);

  // UI state
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  // URL validation state
  const [urlErrors, setUrlErrors] = useState({
    eventLink: null,
    ticketLink: null,
    imageUrl: null,
  });

  // FB link warning state
  const [fbLinkWarning, setFbLinkWarning] = useState('');

  // Duration warning state
  const [durationWarning, setDurationWarning] = useState('');

  // Timeouts for debouncing
  const danceStyleTimeout = useRef(null);
  const artistTimeout = useRef(null);
  const locationTimeout = useRef(null);
  const venueTimeout = useRef(null);
  const organizerTimeout = useRef(null);
  const eventSeriesTimeout = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  // ============================================
  // URL VALIDATION HELPERS
  // ============================================
  // Check if a URL is valid and optionally from trusted domains
  const isValidUrl = (url) => {
    if (!url || !url.trim()) return true; // Empty is allowed
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Known event platforms for event links
  const trustedEventDomains = [
    'facebook.com',
    'fb.me',
    'fb.com',
    'eventbrite.com',
    'eventbrite.co.uk',
    'meetup.com',
    'dice.fm',
    'ra.co',
    'residentadvisor.net',
    'partyflock.nl',
    'danceplanner.com',
    'salsacongress.com',
    'bachatasensual.com',
    'go-kizomba.com',
  ];

  // Check if event link is from a trusted platform (warning only, not blocking)
  const isFromTrustedEventPlatform = (url) => {
    if (!url || !url.trim()) return true;
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
      return trustedEventDomains.some(domain => hostname.includes(domain) || hostname === domain);
    } catch {
      return false;
    }
  };

  // Check if a Facebook URL points to an actual event (has /events/DIGITS in path)
  const checkFacebookEventLink = (url) => {
    if (!url || !url.trim()) return '';
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
      const isFacebook = ['facebook.com', 'fb.com', 'fb.me'].some(d => hostname === d || hostname.endsWith('.' + d));
      if (isFacebook && !/\/events\/\d+/.test(parsed.pathname)) {
        return 'This looks like a Facebook page or group, not an event. Facebook event links follow the format facebook.com/events/123456789. Make sure you\'re copying the link from the specific event page.';
      }
    } catch {
      // Invalid URL handled elsewhere
    }
    return '';
  };

  // Validate URL and return error message if invalid
  const validateUrl = (url, fieldName, checkTrusted = false) => {
    if (!url || !url.trim()) return null; // Empty is allowed

    if (!isValidUrl(url)) {
      return `Please enter a valid URL for ${fieldName}. URLs should start with http:// or https://`;
    }

    if (checkTrusted && !isFromTrustedEventPlatform(url)) {
      // Return warning (not error) for untrusted domains
      return 'warning:This event link is not from a recognized platform (Facebook, Eventbrite, etc.). Please verify the URL is correct.';
    }

    return null;
  };

  // ============================================
  // DANCE STYLE HELPERS (defined early for use in suggestDanceStylesFromName)
  // ============================================
  // Normalize dance style name (capitalize first letter of each word)
  const normalizeDanceStyle = (style) => {
    if (!style) return '';
    return style.trim()
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Check if style already exists (case-insensitive)
  const isDanceStyleSelected = (style, currentStyles) => {
    const normalizedNew = style.toLowerCase().trim();
    return currentStyles.some(existing => existing.toLowerCase() === normalizedNew);
  };

  // Dance style keywords mapping
  const danceStyleKeywords = {
    'salsa': 'Salsa',
    'bachata': 'Bachata',
    'kizomba': 'Kizomba',
    'zouk': 'Zouk',
    'sbk': ['Salsa', 'Bachata', 'Kizomba'],
    'swing': 'Swing',
    'west coast swing': 'West Coast Swing',
    'wcs': 'West Coast Swing',
    'lindy': 'Lindy Hop',
    'lindy hop': 'Lindy Hop',
    'tango': 'Tango',
    'argentine tango': 'Argentine Tango',
    'urban kiz': 'Urban Kiz',
    'semba': 'Semba',
    'afro': 'Afro',
    'kuduro': 'Kuduro',
    'reggaeton': 'Reggaeton',
    'cumbia': 'Cumbia',
    'cha cha': 'Cha Cha',
    'merengue': 'Merengue',
    'mambo': 'Mambo',
    'hustle': 'Hustle',
    'latin': ['Salsa', 'Bachata', 'Cha Cha'],
    'ballroom': 'Ballroom',
        'dominicana': 'Bachata Dominicana',
  };

  // Suggest dance styles based on event name
  const suggestDanceStylesFromName = (name) => {
    if (!name) return;

    const lowerName = name.toLowerCase();
    const suggestedStyles = new Set();

    Object.entries(danceStyleKeywords).forEach(([keyword, styles]) => {
      if (lowerName.includes(keyword)) {
        if (Array.isArray(styles)) {
          styles.forEach(style => suggestedStyles.add(style));
        } else {
          suggestedStyles.add(styles);
        }
      }
    });

    // Add suggested styles that aren't already selected (case-insensitive check)
    if (suggestedStyles.size > 0) {
      setSelectedDanceStyles(prev => {
        const newStyles = [...suggestedStyles].filter(style => !isDanceStyleSelected(style, prev));
        if (newStyles.length > 0) {
          return [...prev, ...newStyles];
        }
        return prev;
      });
    }
  };

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update word count
  useEffect(() => {
    const words = description.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [description]);

  // Duration warning
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = (end - start) / 86400000 + 1;
      if (days <= 2) {
        setDurationWarning('MovenMuse focuses on multi-day events like Festivals, Congresses, and Weekenders. Single-day or overnight events may not be the best fit for this platform.');
      } else {
        setDurationWarning('');
      }
    } else {
      setDurationWarning('');
    }
  }, [startDate, endDate]);

  // Get auth token from localStorage
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // ============================================
  // DANCE STYLE AUTOCOMPLETE
  // ============================================
  const handleDanceStyleInput = (e) => {
    const value = e.target.value;
    setDanceStyleInput(value);

    if (danceStyleTimeout.current) clearTimeout(danceStyleTimeout.current);

    if (value.trim().length < 1) {
      setDanceStyleSuggestions([]);
      return;
    }

    danceStyleTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/dance-styles?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) {
          setDanceStyleSuggestions(data.data);
        }
      } catch (error) {
        console.error('Error fetching dance styles:', error);
      }
    }, 200);
  };

  const handleDanceStyleKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = danceStyleInput.trim();
      if (value) {
        // Split by comma to support multiple entries
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        const newStyles = items
          .filter(item => !isDanceStyleSelected(item, selectedDanceStyles))
          .map(item => normalizeDanceStyle(item));
        if (newStyles.length > 0) {
          setSelectedDanceStyles([...selectedDanceStyles, ...newStyles]);
        }
        setDanceStyleInput('');
        setDanceStyleSuggestions([]);
      }
    }
  };

  const addDanceStyle = (style) => {
    if (style && !isDanceStyleSelected(style, selectedDanceStyles)) {
      const normalized = normalizeDanceStyle(style);
      setSelectedDanceStyles([...selectedDanceStyles, normalized]);
      setDanceStyleInput('');
      setDanceStyleSuggestions([]);
    }
  };

  const removeDanceStyle = (style) => {
    setSelectedDanceStyles(selectedDanceStyles.filter(s => s !== style));
  };

  // ============================================
  // ARTIST AUTOCOMPLETE
  // ============================================
  const handleArtistInput = (e) => {
    const value = e.target.value;
    setArtistInput(value);

    if (artistTimeout.current) clearTimeout(artistTimeout.current);

    if (value.trim().length < 1) {
      setArtistSuggestions([]);
      return;
    }

    artistTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/artists?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) {
          setArtistSuggestions(data.data);
        }
      } catch (error) {
        console.error('Error fetching artists:', error);
      }
    }, 200);
  };

  const handleArtistKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = artistInput.trim();
      if (value) {
        // Split by comma to support multiple entries
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        const newArtists = items.filter(item => !selectedArtists.includes(item));
        if (newArtists.length > 0) {
          setSelectedArtists([...selectedArtists, ...newArtists]);
        }
        setArtistInput('');
        setArtistSuggestions([]);
      }
    }
  };

  const addArtist = (artist) => {
    if (artist && !selectedArtists.includes(artist)) {
      setSelectedArtists([...selectedArtists, artist]);
      setArtistInput('');
      setArtistSuggestions([]);
    }
  };

  const removeArtist = (artist) => {
    setSelectedArtists(selectedArtists.filter(a => a !== artist));
  };

  // ============================================
  // LOCATION SEARCH (OpenStreetMap Nominatim)
  // ============================================
  const handleLocationInput = (e) => {
    const value = e.target.value;
    setLocationSearch(value);

    if (locationTimeout.current) clearTimeout(locationTimeout.current);

    if (value.trim().length < 3) {
      setLocationSuggestions([]);
      return;
    }

    locationTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&addressdetails=1&limit=8`,
          {
            headers: {
              'Accept-Language': 'en',
              'User-Agent': 'MovenMuse/1.0'
            }
          }
        );
        const data = await response.json();
        setLocationSuggestions(data);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    }, 400);
  };

  const selectLocation = (loc) => {
    const address = loc.address || {};
    const cityValue = address.city || address.town || address.village || address.municipality || address.county || '';
    const countryValue = address.country || '';

    setLocationSearch(loc.display_name);
    setCity(cityValue);
    setCountry(countryValue);
    setCoordinates({ lat: parseFloat(loc.lat), lng: parseFloat(loc.lon) });

    // Extract venue name from location (use loc.name first, then address properties)
    const venueFromLocation = loc.name || address.hotel || address.venue || address.building ||
                              address.leisure || address.amenity || address.tourism ||
                              address.shop || address.office || '';
    if (venueFromLocation && !venueName) {
      setVenueName(venueFromLocation);
    }

    // Build address if empty
    if (!venueAddress) {
      const parts = [];
      if (address.road) parts.push(address.road);
      if (address.house_number && parts.length > 0) parts[0] = address.house_number + ' ' + parts[0];
      if (address.postcode) parts.push(address.postcode);
      setVenueAddress(parts.join(', '));
    }

    setLocationSuggestions([]);
  };

  // ============================================
  // VENUE AUTOCOMPLETE
  // ============================================
  const handleVenueInput = (e) => {
    const value = e.target.value;
    setVenueName(value);

    if (venueTimeout.current) clearTimeout(venueTimeout.current);

    if (value.trim().length < 2) {
      setVenueSuggestions([]);
      return;
    }

    venueTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/venues?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) {
          setVenueSuggestions(data.data);
        }
      } catch (error) {
        console.error('Error fetching venues:', error);
      }
    }, 300);
  };

  const selectVenue = (venue) => {
    setVenueName(venue.name);
    if (venue.address) setVenueAddress(venue.address);
    if (venue.city) setCity(venue.city);
    if (venue.country) setCountry(venue.country);
    setVenueSuggestions([]);
  };

  // ============================================
  // ORGANIZER AUTOCOMPLETE (Multiple)
  // ============================================
  const handleOrganizerInput = (e) => {
    const value = e.target.value;
    setOrganizerInput(value);

    if (organizerTimeout.current) clearTimeout(organizerTimeout.current);

    if (value.trim().length < 2) {
      setOrganizerSuggestions([]);
      return;
    }

    organizerTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/organizers?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) {
          setOrganizerSuggestions(data.data);
        }
      } catch (error) {
        console.error('Error fetching organizers:', error);
      }
    }, 200);
  };

  const handleOrganizerKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = organizerInput.trim();
      if (value) {
        // Split by comma to support multiple entries
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        const newOrganizers = items.filter(item => !selectedOrganizers.includes(item));
        if (newOrganizers.length > 0) {
          setSelectedOrganizers([...selectedOrganizers, ...newOrganizers]);
        }
        setOrganizerInput('');
        setOrganizerSuggestions([]);
      }
    }
  };

  const addOrganizer = (name) => {
    if (name && !selectedOrganizers.includes(name)) {
      setSelectedOrganizers([...selectedOrganizers, name]);
      setOrganizerInput('');
      setOrganizerSuggestions([]);
    }
  };

  const removeOrganizer = (name) => {
    setSelectedOrganizers(selectedOrganizers.filter(o => o !== name));
  };

  // ============================================
  // EVENT SERIES AUTOCOMPLETE
  // ============================================
  const handleEventSeriesInput = (e) => {
    const value = e.target.value;
    setEventSeries(value);

    if (eventSeriesTimeout.current) clearTimeout(eventSeriesTimeout.current);

    if (value.trim().length < 2) {
      setEventSeriesSuggestions([]);
      return;
    }

    eventSeriesTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/event-series?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) {
          setEventSeriesSuggestions(data.data);
        }
      } catch (error) {
        console.error('Error fetching event series:', error);
      }
    }, 200);
  };

  const selectEventSeries = (name) => {
    setEventSeries(name);
    setEventSeriesSuggestions([]);
  };

  // ============================================
  // IMAGE PREVIEW
  // ============================================
  const handleImageUrlChange = (e) => {
    const value = e.target.value;
    setEventImageUrl(value);
    // Validate on blur or after typing
    if (value && !isValidUrl(value)) {
      setUrlErrors(prev => ({ ...prev, imageUrl: 'Please enter a valid URL' }));
    } else {
      setUrlErrors(prev => ({ ...prev, imageUrl: null }));
    }
  };

  // ============================================
  // URL FIELD HANDLERS WITH VALIDATION
  // ============================================
  const handleEventLinkChange = (e) => {
    const value = e.target.value;
    setEventLink(value);
    // Clear error while typing, validate on blur
  };

  const handleEventLinkBlur = () => {
    if (eventLink && eventLink.trim()) {
      const error = validateUrl(eventLink, 'event link', true);
      if (error && !error.startsWith('warning:')) {
        setUrlErrors(prev => ({ ...prev, eventLink: error }));
        setFbLinkWarning('');
      } else if (error && error.startsWith('warning:')) {
        setUrlErrors(prev => ({ ...prev, eventLink: error.substring(8) }));
        setFbLinkWarning('');
      } else {
        setUrlErrors(prev => ({ ...prev, eventLink: null }));
        // Check for FB-specific event link pattern
        const fbWarning = checkFacebookEventLink(eventLink);
        setFbLinkWarning(fbWarning);
      }
    } else {
      setUrlErrors(prev => ({ ...prev, eventLink: null }));
      setFbLinkWarning('');
    }
  };

  const handleTicketLinkChange = (e) => {
    const value = e.target.value;
    setTicketLink(value);
  };

  const handleTicketLinkBlur = () => {
    if (ticketLink && ticketLink.trim()) {
      const error = validateUrl(ticketLink, 'ticket link');
      if (error && !error.startsWith('warning:')) {
        setUrlErrors(prev => ({ ...prev, ticketLink: error }));
      } else {
        setUrlErrors(prev => ({ ...prev, ticketLink: null }));
      }
    } else {
      setUrlErrors(prev => ({ ...prev, ticketLink: null }));
    }
  };

  const handleImageUrlBlur = () => {
    if (eventImageUrl && eventImageUrl.trim()) {
      const error = validateUrl(eventImageUrl, 'image URL');
      if (error && !error.startsWith('warning:')) {
        setUrlErrors(prev => ({ ...prev, imageUrl: error }));
      } else {
        setUrlErrors(prev => ({ ...prev, imageUrl: null }));
      }
    } else {
      setUrlErrors(prev => ({ ...prev, imageUrl: null }));
    }
  };

  // ============================================
  // FORM SUBMISSION
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setStatusMessage({ text: 'Please sign in to add events.', type: 'error' });
      return;
    }

    setIsSubmitting(true);

    // Validate required fields
    if (!eventName || !startDate || !endDate || !description || !venueName || !city || !country) {
      setStatusMessage({ text: 'Please fill in all required fields.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate organizers (mandatory)
    if (selectedOrganizers.length === 0) {
      setStatusMessage({ text: 'Please add at least one organizer.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate dance styles (mandatory)
    if (selectedDanceStyles.length === 0) {
      setStatusMessage({ text: 'Please add at least one dance style.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate featured artists (mandatory)
    if (selectedArtists.length === 0) {
      setStatusMessage({ text: 'Please add at least one featured artist or instructor.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate word count
    if (wordCount < 20) {
      setStatusMessage({ text: `Description must be at least 20 words. Currently: ${wordCount} words.`, type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate dates
    if (new Date(endDate) < new Date(startDate)) {
      setStatusMessage({ text: 'End date cannot be before the start date.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate event link URL
    if (eventLink && eventLink.trim()) {
      const eventLinkError = validateUrl(eventLink, 'Facebook Event Link', true);
      if (eventLinkError) {
        if (eventLinkError.startsWith('warning:')) {
          // Show warning but allow to proceed
          const proceed = window.confirm(eventLinkError.substring(8) + '\n\nDo you want to continue anyway?');
          if (!proceed) {
            setIsSubmitting(false);
            return;
          }
        } else {
          setStatusMessage({ text: eventLinkError, type: 'error' });
          setIsSubmitting(false);
          return;
        }
      }
    }

    // Validate ticket link URL
    if (ticketLink && ticketLink.trim()) {
      const ticketLinkError = validateUrl(ticketLink, 'Ticket Purchase Link');
      if (ticketLinkError && !ticketLinkError.startsWith('warning:')) {
        setStatusMessage({ text: ticketLinkError, type: 'error' });
        setIsSubmitting(false);
        return;
      }
    }

    // Validate image URL
    if (eventImageUrl && eventImageUrl.trim()) {
      const imageUrlError = validateUrl(eventImageUrl, 'Image URL');
      if (imageUrlError && !imageUrlError.startsWith('warning:')) {
        setStatusMessage({ text: imageUrlError, type: 'error' });
        setIsSubmitting(false);
        return;
      }
    }

    const eventData = {
      title: (eventName || '').trim(),
      eventlink: (eventLink || '').trim() || 'manual-entry',
      startDateTime: new Date(startDate + 'T00:00:00').toISOString(),
      endDateTime: new Date(endDate + 'T23:59:59').toISOString(),
      description: (description || '').trim(),
      location: (venueName || '').trim(),
      venueAddress: (venueAddress || '').trim(),
      city: (city || '').trim(),
      country: (country || '').trim(),
      coordinates: coordinates,
      category: selectedDanceStyles,
      eventImage: (eventImageUrl || '').trim(),
      organizers: selectedOrganizers,
      organizer: selectedOrganizers[0] || '', // For backward compatibility
      ticketLink: (ticketLink || '').trim() || undefined,
      price: (price || '').trim(),
      currency: currency,
      peopleResponse: (peopleResponse || '').trim(),
      artists: selectedArtists,
      eventSeries: (eventSeries || '').trim() || null,
      edition: edition ? parseInt(edition, 10) : null
    };

    try {
      const response = await fetch(`${API_BASE}/events/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(eventData)
      });

      const data = await response.json();

      if (data.success) {
        setStatusMessage({ text: 'Event added successfully! Redirecting to event page...', type: 'success' });
        const eventId = data.data?._id || data.data?.id;
        setTimeout(() => {
          if (eventId) {
            navigate(`/event/${eventId}`);
          } else {
            navigate('/events');
          }
        }, 2000);
      } else {
        setStatusMessage({ text: data.message || 'Failed to add event. Please try again.', type: 'error' });
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error adding event:', error);
      setStatusMessage({ text: 'Something went wrong. Please try again.', type: 'error' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="import-event-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="page-badge">IMPORT EVENT</div>
          <h1 className="page-title">Add a Dance Event</h1>
          <p className="page-subtitle">Share a dance event with the Move n Muse community. Paste a Facebook event link or fill in the details manually.</p>
        </div>
      </section>

      {/* Import Form */}
      <div className="import-container">
        <div className="import-card">
          <form onSubmit={handleSubmit}>
            {/* Event Link Section */}
            <div className="event-link-section">
              <label className="form-label">Facebook Event Link</label>
              <div className="event-link-input">
                <input
                  type="url"
                  className={`form-control ${urlErrors.eventLink ? 'is-invalid' : ''}`}
                  value={eventLink}
                  onChange={handleEventLinkChange}
                  onBlur={handleEventLinkBlur}
                  placeholder="https://www.facebook.com/events/..."
                />
                <button type="button" className="btn-fetch" disabled>
                  <i className="bi bi-cloud-download"></i> Auto-fill (Coming Soon)
                </button>
              </div>
              {urlErrors.eventLink ? (
                <p className="url-validation-error"><i className="bi bi-exclamation-circle"></i> {urlErrors.eventLink}</p>
              ) : fbLinkWarning ? (
                <div className="fb-link-warning">
                  <i className="bi bi-exclamation-triangle"></i> {fbLinkWarning}
                </div>
              ) : (
                <p className="fetch-hint"><i className="bi bi-info-circle"></i> Paste the Facebook event URL. Manual entry required for now - auto-fetch coming soon!</p>
              )}
            </div>

            {/* 1. Event Name, Series, Edition */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-calendar-event"></i> Event Details</h3>

              <div className="mb-3">
                <label className="form-label">Event Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  onBlur={(e) => suggestDanceStylesFromName(e.target.value)}
                  placeholder="e.g., Bachata Stars Festival 2025"
                  required
                />
              </div>

              <div className="date-time-row mb-3">
                <div>
                  <label className="form-label">Event Series <small className="text-muted">(if recurring)</small></label>
                  <div className="autocomplete-input-wrapper">
                    <input
                      type="text"
                      className="form-control"
                      value={eventSeries}
                      onChange={handleEventSeriesInput}
                      onBlur={() => setTimeout(() => setEventSeriesSuggestions([]), 200)}
                      placeholder="e.g., Bachata Stars Festival"
                      autoComplete="off"
                    />
                    {eventSeriesSuggestions.length > 0 && (
                      <div className="autocomplete-suggestions show">
                        {eventSeriesSuggestions.map((series, idx) => (
                          <div key={idx} className="autocomplete-suggestion" onClick={() => selectEventSeries(series.name)}>
                            <div>
                              <span className="suggestion-name">{series.name}</span>
                              {series.editions && series.editions.length > 0 && (
                                <small className="text-muted"><br/>Editions: {series.editions.sort((a,b) => a-b).join(', ')}</small>
                              )}
                            </div>
                            <span className="suggestion-count">{series.count} event{series.count !== 1 ? 's' : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <small className="form-text text-muted">Link this to other editions of the same event</small>
                </div>
                <div>
                  <label className="form-label">Edition Number</label>
                  <input
                    type="number"
                    className="form-control"
                    value={edition}
                    onChange={(e) => setEdition(e.target.value)}
                    placeholder="e.g., 5"
                    min="1"
                  />
                  <small className="form-text text-muted">e.g., 5th edition</small>
                </div>
              </div>
            </div>

            {/* 2. Dance Styles */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-music-note-beamed"></i> Dance Styles <span className="required">*</span></h3>

              <div className="dance-styles-container">
                {selectedDanceStyles.map((style, idx) => (
                  <span key={idx} className="dance-style-tag">
                    {style}
                    <span className="remove-tag" onClick={() => removeDanceStyle(style)}>&times;</span>
                  </span>
                ))}
              </div>

              <div className="autocomplete-input-wrapper">
                <input
                  type="text"
                  className="form-control"
                  value={danceStyleInput}
                  onChange={handleDanceStyleInput}
                  onKeyDown={handleDanceStyleKeydown}
                  onBlur={() => setTimeout(() => setDanceStyleSuggestions([]), 200)}
                  placeholder="Start typing a dance style (e.g., Kizomba, Urban Kiz)..."
                  autoComplete="off"
                />
                {danceStyleSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions show">
                    {danceStyleSuggestions.map((item, idx) => (
                      <div key={idx} className="autocomplete-suggestion" onClick={() => addDanceStyle(item.name)}>
                        <span className="suggestion-name">{item.name}</span>
                        <span className="suggestion-count">{item.count} event{item.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-text text-muted">Press Enter to add. Use commas to add multiple at once (e.g., Kizomba, Bachata, Salsa)</small>
              </div>
            </div>

            {/* 3. Event Dates */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-calendar-range"></i> Event Dates <span className="required">*</span></h3>

              <div className="date-range-picker">
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <span className="date-separator">to</span>
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>
              {durationWarning && (
                <div className="duration-warning">
                  <i className="bi bi-exclamation-triangle"></i> {durationWarning}
                </div>
              )}
            </div>

            {/* 4. People Interested */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-people"></i> Engagement</h3>
              <div className="mb-3">
                <label className="form-label">People Interested/Going (from FB)</label>
                <input
                  type="number"
                  className="form-control"
                  value={peopleResponse}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || (Number(val) >= 0 && Number(val) <= 9999)) {
                      setPeopleResponse(val);
                    }
                  }}
                  min="0"
                  max="9999"
                  placeholder="e.g., 500"
                />
              </div>
            </div>

            {/* 5. Organizers */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-person-badge"></i> Organizers <span className="required">*</span></h3>
              <div className="organizers-container">
                {selectedOrganizers.map((org, idx) => (
                  <span key={idx} className="organizer-tag">
                    {org}
                    <span className="remove-tag" onClick={() => removeOrganizer(org)}>&times;</span>
                  </span>
                ))}
              </div>
              <div className="autocomplete-input-wrapper">
                <input
                  type="text"
                  className="form-control"
                  value={organizerInput}
                  onChange={handleOrganizerInput}
                  onKeyDown={handleOrganizerKeydown}
                  onBlur={() => setTimeout(() => setOrganizerSuggestions([]), 200)}
                  placeholder="Start typing an organizer name..."
                  autoComplete="off"
                />
                {organizerSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions show">
                    {organizerSuggestions.map((org, idx) => (
                      <div key={idx} className="autocomplete-suggestion" onClick={() => addOrganizer(org.name)}>
                        <span className="suggestion-name">{org.name}</span>
                        <span className="suggestion-count">{org.count} event{org.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-text text-muted">Press Enter to add. Use commas to add multiple at once (e.g., Org1, Org2, Org3)</small>
              </div>
            </div>

            {/* 6. Location */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-geo-alt"></i> Location</h3>

              <div className="mb-3">
                <label className="form-label">Search Location <span className="required">*</span></label>
                <div className="autocomplete-input-wrapper">
                  <input
                    type="text"
                    className="form-control"
                    value={locationSearch}
                    onChange={handleLocationInput}
                    onBlur={() => setTimeout(() => setLocationSuggestions([]), 200)}
                    placeholder="Start typing an address, venue, or city..."
                    autoComplete="off"
                  />
                  {locationSuggestions.length > 0 && (
                    <div className="autocomplete-suggestions show">
                      {locationSuggestions.map((loc, idx) => (
                        <div key={idx} className="autocomplete-suggestion" onClick={() => selectLocation(loc)}>
                          <span className="suggestion-name">
                            {loc.display_name.length > 60 ? loc.display_name.substring(0, 57) + '...' : loc.display_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="form-text text-muted"><i className="bi bi-search"></i> Search for a place to auto-fill city and country</small>
              </div>

              <div className="mb-3">
                <label className="form-label">Venue Name <span className="required">*</span></label>
                <div className="autocomplete-input-wrapper">
                  <input
                    type="text"
                    className="form-control"
                    value={venueName}
                    onChange={handleVenueInput}
                    onBlur={() => setTimeout(() => setVenueSuggestions([]), 200)}
                    placeholder="e.g., Grand Ballroom Hotel"
                    required
                    autoComplete="off"
                  />
                  {venueSuggestions.length > 0 && (
                    <div className="autocomplete-suggestions show">
                      {venueSuggestions.map((venue, idx) => (
                        <div key={idx} className="autocomplete-suggestion" onClick={() => selectVenue(venue)}>
                          <div>
                            <span className="suggestion-name">{venue.name}</span>
                            <div className="venue-location">{[venue.city, venue.country].filter(Boolean).join(', ')}</div>
                          </div>
                          <span className="suggestion-count">{venue.eventCount} event{venue.eventCount !== 1 ? 's' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Venue Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={venueAddress}
                  onChange={(e) => setVenueAddress(e.target.value)}
                  placeholder="Full street address"
                />
              </div>

              <div className="date-time-row mb-3">
                <div>
                  <label className="form-label">City <span className="required">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Auto-filled from search or type manually"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Country <span className="required">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Auto-filled from search or type manually"
                    required
                  />
                </div>
              </div>
            </div>

            {/* 7. Description */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-text-paragraph"></i> Description <span className="required">*</span></h3>
              <div className="mb-3">
                <label className="form-label">Event Description <small>(minimum 20 words)</small></label>
                <textarea
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="3"
                  placeholder="Describe the event, what attendees can expect, workshops, parties, etc."
                  required
                ></textarea>
                <div className={`word-counter ${wordCount >= 20 ? 'success' : 'error'}`}>
                  {wordCount} / 20 words
                </div>
              </div>
            </div>

            {/* 8. Event Image */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-image"></i> Event Image</h3>

              <div className="mb-3">
                <label className="form-label">Image URL</label>
                <input
                  type="url"
                  className={`form-control ${urlErrors.imageUrl ? 'is-invalid' : ''}`}
                  value={eventImageUrl}
                  onChange={handleImageUrlChange}
                  onBlur={handleImageUrlBlur}
                  placeholder="https://example.com/event-image.jpg"
                />
                {urlErrors.imageUrl && (
                  <small className="url-validation-error"><i className="bi bi-exclamation-circle"></i> {urlErrors.imageUrl}</small>
                )}
              </div>

              <div className="image-upload-area">
                <i className="bi bi-cloud-arrow-up"></i>
                <p>Or drag & drop an image here<br/><small>(Coming soon)</small></p>
              </div>
              {eventImageUrl && (
                <img
                  src={eventImageUrl}
                  alt="Event preview"
                  className="image-preview"
                  style={{ display: 'block' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>

            {/* 9. Featured Artists (Required) */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-star"></i> Featured Artists / Instructors <span className="required">*</span></h3>

              <div className="artists-container">
                {selectedArtists.map((artist, idx) => (
                  <span key={idx} className="artist-tag">
                    {artist}
                    <span className="remove-tag" onClick={() => removeArtist(artist)}>&times;</span>
                  </span>
                ))}
              </div>

              <div className="autocomplete-input-wrapper">
                <input
                  type="text"
                  className="form-control"
                  value={artistInput}
                  onChange={handleArtistInput}
                  onKeyDown={handleArtistKeydown}
                  onBlur={() => setTimeout(() => setArtistSuggestions([]), 200)}
                  placeholder="Start typing an artist name..."
                  autoComplete="off"
                />
                {artistSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions show">
                    {artistSuggestions.map((item, idx) => (
                      <div key={idx} className="autocomplete-suggestion" onClick={() => addArtist(item.name)}>
                        <span className="suggestion-name">{item.name}</span>
                        <span className="suggestion-count">{item.count} event{item.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-text text-muted">Press Enter to add. Use commas to add multiple at once (e.g., DJ Hugo, DJ Lisa, Korke y Judith)</small>
              </div>
            </div>

            {/* 10. Pricing & Tickets */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-tag"></i> Pricing & Tickets</h3>

              <div className="date-time-row mb-3">
                <div className="price-currency-group">
                  <label className="form-label">Price Range</label>
                  <div className="price-currency-inputs">
                    <select
                      className="form-control currency-select"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      {currencyOptions.map(opt => (
                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="form-control price-input"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="e.g., 150 - 300 or Free"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Ticket Purchase Link</label>
                <input
                  type="url"
                  className={`form-control ${urlErrors.ticketLink ? 'is-invalid' : ''}`}
                  value={ticketLink}
                  onChange={handleTicketLinkChange}
                  onBlur={handleTicketLinkBlur}
                  placeholder="https://tickets.example.com/event"
                />
                {urlErrors.ticketLink ? (
                  <small className="url-validation-error"><i className="bi bi-exclamation-circle"></i> {urlErrors.ticketLink}</small>
                ) : (
                  <small className="form-text text-muted">Direct link to purchase passes/tickets</small>
                )}
              </div>
            </div>

            {/* Status Messages - shown above submit button */}
            {statusMessage.text && (
              <div className={`status-message ${statusMessage.type}`}>
                {statusMessage.text}
              </div>
            )}

            {/* Submit Section */}
            <div className="submit-section">
              <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>Cancel</button>
              <button type="submit" className="btn-submit" disabled={isSubmitting || !user}>
                {isSubmitting ? (
                  <><i className="bi bi-hourglass-split"></i> Adding Event...</>
                ) : (
                  <><i className="bi bi-plus-circle"></i> Add Event</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImportEventPage;
